import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { mutualisteAPI, parameterAPI } from '../../services/api';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';

const AddAyantDroitModal = ({ adherentId, isOpen, onClose, onSuccess }) => {
  // Récupérer les options de paramétrage
  const { data: parametersData, isLoading: parametersLoading, error: parametersError } = useQuery(
    'parameters-options-v3', // Même cache key que EditBeneficiary
    () => parameterAPI.getActiveOptions(),
    {
      staleTime: 0, // Pas de cache pour forcer le rechargement
      cacheTime: 0, // Pas de cache
      refetchOnMount: true,
      onError: (error) => {
        console.error('❌ [AddAyantDroit] Erreur récupération options:', error);
      }
    }
  );

  const [formData, setFormData] = useState({
    matricule: '',
    nom: '',
    prenoms: '',
    genre: '',
    dateNaissance: '',
    lieuNaissance: '',
    nationalite: 'Ivoirienne',
    
    // Informations de résidence (peuvent être les mêmes que l'adhérent)
    adresseResidence: '',
    ville: '',
    codePostal: '',
    pays: "Côte d'Ivoire",
    
    // Contact (peut être le même que l'adhérent)
    mobile1: '',
    mobile2: '',
    emailPersonnel: '',
    adressePostale: '',
    
    // Lien de parenté obligatoire pour les ayants-droit
    lienParente: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Effacer l'erreur pour ce champ
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Champs obligatoires
    if (!formData.matricule.trim()) newErrors.matricule = 'Le matricule est obligatoire';
    if (!formData.nom.trim()) newErrors.nom = 'Le nom est obligatoire';
    if (!formData.prenoms.trim()) newErrors.prenoms = 'Les prénoms sont obligatoires';
    if (!formData.genre) newErrors.genre = 'Le genre est obligatoire';
    if (!formData.dateNaissance) newErrors.dateNaissance = 'La date de naissance est obligatoire';
    if (!formData.adresseResidence.trim()) newErrors.adresseResidence = 'L\'adresse de résidence est obligatoire';
    if (!formData.ville.trim()) newErrors.ville = 'La ville est obligatoire';
    if (!formData.mobile1.trim()) newErrors.mobile1 = 'Le mobile principal est obligatoire';
    if (!formData.lienParente.trim()) newErrors.lienParente = 'Le lien de parenté est obligatoire';

    // Validation matricule (format: 6-7 chiffres + 1 lettre + 4 chiffres)
    if (formData.matricule && !/^\d{6,7}[A-Z]\d{4}$/.test(formData.matricule)) {
      newErrors.matricule = 'Format de matricule invalide (ex: 1234567A1234)';
    }

    // Validation email
    if (formData.emailPersonnel && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailPersonnel)) {
      newErrors.emailPersonnel = 'Format d\'email invalide';
    }

    // Validation téléphone (format simple)
    if (formData.mobile1 && !/^[0-9+\-\s()]{8,}$/.test(formData.mobile1)) {
      newErrors.mobile1 = 'Format de téléphone invalide';
    }
    if (formData.mobile2 && !/^[0-9+\-\s()]{8,}$/.test(formData.mobile2)) {
      newErrors.mobile2 = 'Format de téléphone invalide';
    }

    // Validation âge (ne peut pas être né dans le futur)
    if (formData.dateNaissance) {
      const today = new Date();
      const birthDate = new Date(formData.dateNaissance);
      if (birthDate > today) {
        newErrors.dateNaissance = 'La date de naissance ne peut pas être dans le futur';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    setIsLoading(true);
    
    try {
      // Préparer les données pour l'API
      const ayantDroitData = {
        ...formData,
        dateNaissance: new Date(formData.dateNaissance).toISOString(),
        matricule: formData.matricule.toUpperCase() // S'assurer que la lettre est en majuscule
      };

      await mutualisteAPI.addAyantDroit(adherentId, ayantDroitData);
      
      toast.success('Ayant-droit ajouté avec succès');
      onSuccess();
      handleReset();
      onClose();
      
    } catch (error) {
      console.error('Erreur lors de l\'ajout:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'ajout de l\'ayant-droit');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      matricule: '',
      nom: '',
      prenoms: '',
      genre: '',
      dateNaissance: '',
      lieuNaissance: '',
      nationalite: 'Ivoirienne',
      adresseResidence: '',
      ville: '',
      codePostal: '',
      pays: "Côte d'Ivoire",
      mobile1: '',
      mobile2: '',
      emailPersonnel: '',
      adressePostale: '',
      lienParente: ''
    });
    setErrors({});
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-lg bg-white max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">
              Ajouter un ayant-droit
            </h3>
            <p className="text-gray-600 mt-1">
              Ajoutez un nouveau bénéficiaire à votre couverture mutualiste
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Informations de base */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Informations de base</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Matricule *
                </label>
                <input
                  type="text"
                  name="matricule"
                  value={formData.matricule}
                  onChange={handleInputChange}
                  placeholder="ex: 1234567A1234"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.matricule ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.matricule && <p className="text-red-500 text-xs mt-1">{errors.matricule}</p>}
                <p className="text-xs text-gray-500 mt-1">Format: 6-7 chiffres + 1 lettre + 4 chiffres</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lien de parenté *
                </label>
                <select
                  name="lienParente"
                  value={formData.lienParente}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.lienParente ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={parametersLoading}
                >
                  <option value="">
                    {parametersLoading ? 'Chargement...' : 
                     parametersError ? 'Erreur de chargement' :
                     parametersData?.data?.data?.lienParente?.length ? 'Sélectionner le lien' : 'Aucune donnée'}
                  </option>
                  {parametersData?.data?.data?.lienParente?.map((lien) => (
                    <option key={lien.id} value={lien.id}>
                      {lien.libelle}
                    </option>
                  ))}
                </select>
                {errors.lienParente && <p className="text-red-500 text-xs mt-1">{errors.lienParente}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom *
                </label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.nom ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.nom && <p className="text-red-500 text-xs mt-1">{errors.nom}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prénoms *
                </label>
                <input
                  type="text"
                  name="prenoms"
                  value={formData.prenoms}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.prenoms ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.prenoms && <p className="text-red-500 text-xs mt-1">{errors.prenoms}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Genre *
                </label>
                <select
                  name="genre"
                  value={formData.genre}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.genre ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={parametersLoading}
                >
                  <option value="">
                    {parametersLoading ? 'Chargement...' : 
                     parametersError ? 'Erreur de chargement' :
                     parametersData?.data?.data?.genres?.length ? 'Sélectionner' : 'Aucune donnée'}
                  </option>
                  {parametersData?.data?.data?.genres?.map((genre) => (
                    <option key={genre.id} value={genre.id}>
                      {genre.libelle}
                    </option>
                  ))}
                </select>
                {errors.genre && <p className="text-red-500 text-xs mt-1">{errors.genre}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de naissance *
                </label>
                <input
                  type="date"
                  name="dateNaissance"
                  value={formData.dateNaissance}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.dateNaissance ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.dateNaissance && <p className="text-red-500 text-xs mt-1">{errors.dateNaissance}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lieu de naissance
                </label>
                <input
                  type="text"
                  name="lieuNaissance"
                  value={formData.lieuNaissance}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nationalité
                </label>
                <input
                  type="text"
                  name="nationalite"
                  value={formData.nationalite}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Adresse de résidence */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Adresse de résidence</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse de résidence *
                </label>
                <textarea
                  name="adresseResidence"
                  value={formData.adresseResidence}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="Adresse complète de résidence"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.adresseResidence ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.adresseResidence && <p className="text-red-500 text-xs mt-1">{errors.adresseResidence}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ville *
                </label>
                <input
                  type="text"
                  name="ville"
                  value={formData.ville}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.ville ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.ville && <p className="text-red-500 text-xs mt-1">{errors.ville}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code postal
                </label>
                <input
                  type="text"
                  name="codePostal"
                  value={formData.codePostal}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pays
                </label>
                <input
                  type="text"
                  name="pays"
                  value={formData.pays}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Informations de contact</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile principal *
                </label>
                <input
                  type="tel"
                  name="mobile1"
                  value={formData.mobile1}
                  onChange={handleInputChange}
                  placeholder="ex: 0701234567"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.mobile1 ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.mobile1 && <p className="text-red-500 text-xs mt-1">{errors.mobile1}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile secondaire
                </label>
                <input
                  type="tel"
                  name="mobile2"
                  value={formData.mobile2}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.mobile2 ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.mobile2 && <p className="text-red-500 text-xs mt-1">{errors.mobile2}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email personnel
                </label>
                <input
                  type="email"
                  name="emailPersonnel"
                  value={formData.emailPersonnel}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.emailPersonnel ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.emailPersonnel && <p className="text-red-500 text-xs mt-1">{errors.emailPersonnel}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse postale
                </label>
                <input
                  type="text"
                  name="adressePostale"
                  value={formData.adressePostale}
                  onChange={handleInputChange}
                  placeholder="ex: BP 1234 Abidjan"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Ajout en cours...' : 'Ajouter l\'ayant-droit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAyantDroitModal;
